import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { LocalDate } from "@js-joda/core";
import type { TipDetails } from "model/Tip";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { MonthlyReportTotalsRow } from "./tips.monthlyReportTotalsRow.component";
import { MonthlyReportWorkerRow } from "./tips.monthlyReportWorkerRow.component";
import { formatMonthTitle } from "../logic/tips.format.logic";
import {
  buildMonthlyReports,
  buildMonthlyReportTotals,
} from "./tips.monthlyReports.logic";
import {
  MonthlyEmptyState,
  MonthlyReportCard,
  MonthlyReportDescription,
  MonthlyReportHeader,
  MonthlyReportRows,
  MonthlyReportTitle,
} from "./tips.monthlyReports.styled";

interface TipsMonthlyReportsProps {
  readonly currentDate: LocalDate;
  readonly tips: readonly TipDetails[];
}

export const TipsMonthlyReports = (
  props: TipsMonthlyReportsProps,
): JSX.Element => {
  const { i18n, t } = useTranslation();
  const [{ companyWorkerImages }] = useCompanyStore();
  const [expandedWorkerIds, setExpandedWorkerIds] = useState<
    ReadonlySet<number>
  >(() => new Set());
  const rows = useMemo(() => buildMonthlyReports(props.tips), [props.tips]);
  const totals = useMemo(() => buildMonthlyReportTotals(rows), [rows]);
  const monthTitle = formatMonthTitle({
    date: props.currentDate,
    locale: i18n.language,
  });
  const hourLabel = t("Tips.HourShort");
  const minuteLabel = t("Tips.MinuteShort");

  const toggleExpanded = (workerId: number): void => {
    setExpandedWorkerIds((currentIds) => {
      const nextIds = new Set(currentIds);
      if (nextIds.has(workerId)) {
        nextIds.delete(workerId);
      } else {
        nextIds.add(workerId);
      }
      return nextIds;
    });
  };

  return (
    <MonthlyReportCard>
      <MonthlyReportHeader>
        <MonthlyReportTitle>{monthTitle}</MonthlyReportTitle>
        <MonthlyReportDescription>
          {t("MonthlyTipReport.Description")}
        </MonthlyReportDescription>
      </MonthlyReportHeader>

      {rows.length === 0 ? (
        <MonthlyEmptyState>
          {t("MonthlyTipReport.NoTimeReportsToShow")}
        </MonthlyEmptyState>
      ) : (
        <MonthlyReportRows>
          {rows.map((row) => {
            const workerImage = row.externalId
              ? companyWorkerImages[row.externalId]
              : null;
            const avatarUrl =
              workerImage && workerImage !== "no-image" ? workerImage : null;

            return (
              <MonthlyReportWorkerRow
                key={row.workerId}
                avatarUrl={avatarUrl}
                hourLabel={hourLabel}
                isExpanded={expandedWorkerIds.has(row.workerId)}
                locale={i18n.language}
                minuteLabel={minuteLabel}
                row={row}
                onToggleExpanded={toggleExpanded}
              />
            );
          })}

          <MonthlyReportTotalsRow
            hourLabel={hourLabel}
            minuteLabel={minuteLabel}
            totals={totals}
          />
        </MonthlyReportRows>
      )}
    </MonthlyReportCard>
  );
};
